// Minimal, framework-agnostic CardConnect HIFT adapter
// Usage:
//   const detach = attach(containerEl, { env: 'uat'|'prod', onToken, onError })
//   detach() to cleanup

function buildIframeSrc(env) {
  console.log('buildIframeSrc', env);
  const subdomain = env === 'uat' ? 'boltgw-uat' : 'boltgw';

  const css = `
body{
  margin:0;
  font-family:Inter,system-ui,Arial,sans-serif;
  white-space:nowrap;               /* prevents wrap → avoids clipping */
}
*,*::before,*::after{box-sizing:border-box}

label{
  margin:6px 0 4px 5px;
  font-size:.9rem;
  color:#374151;
  vertical-align:middle;
}

input,select{
  border:1px solid #d1d5db;
  border-radius:10px;
  height:40px;
  padding:0 12px;
  font-size:16px;
  outline:none;
  background:#fff;
}
select{padding-right:16px; -webkit-appearance:none;}

input:focus,select:focus{
  border-color:#14CEFB; box-shadow:0 0 0 3px rgba(20,206,251,.2);
}
#ccnumfield{margin-bottom:4px;}
#ccexpirymonth{width:6ch; display:inline-block; vertical-align:middle;}
#ccexiryyear,#ccexpiryyear{width:8ch; display:inline-block; margin-left:8px; vertical-align:middle;}
#cccvvlabel{margin-left:40px; margin-top:2px; display:inline-block; vertical-align:middle;}
#cccvvfield{width:8ch; margin-left:8px; display:inline-block; vertical-align:middle;}
  `.trim();

  const params = new URLSearchParams({
    orientation: 'horizontal', // consider 'vertical' on narrow screens
    useexpiry: 'true',
    usecvv: 'true',
    formatinput: 'true',
    tokenizewheninactive: 'true',
    inactivityto: '500',
    invalidinputevent: 'true',
    css,
  });

  return `https://${subdomain}.cardconnect.com/itoke/ajax-tokenizer.html?${params.toString()}`;
}

function parseMessageData(data) {
  console.log('parseMessageData', data);
  try {
    const msg = typeof data === 'string' ? JSON.parse(data) : data;
    if (!msg || typeof msg !== 'object') return null;
    // Treat validation/invalid flags as error
    if (msg.invalid === true || msg.error || msg.validationError) return { error: true };

    // CardConnect HIFT may send the token under `message` (FlexPay) or `token` (API-style)
    let token = null;
    if (typeof msg.message === 'string' && msg.message && msg.message !== 'No error') {
      token = msg.message;
    } else if (typeof msg.token === 'string' && msg.token) {
      token = msg.token;
    }

    // Expiry can vary; coerce to a digit string if present, but do not enforce shape
    let expiry = msg.expiry;
    if (expiry !== undefined && expiry !== null) {
      if (typeof expiry !== 'string') expiry = String(expiry);
      expiry = expiry.replace(/\D/g, '');
      if (!expiry) expiry = undefined;
    }

    if (token) return { token, expiry };
    return null;
  } catch (_e) {
    return null;
  }
}

export function attach(containerEl, { env = 'prod', onToken, onError } = {}) {
  console.log('attach', containerEl, { env, onToken, onError });
  if (!containerEl) throw new Error('CardConnect adapter: containerEl is required');

  // Clean container
  while (containerEl.firstChild) containerEl.removeChild(containerEl.firstChild);

  // Telemetry (non-sensitive)
  const sessionId = Math.random().toString(36).slice(2);
  const attachTs = Date.now();
  let msgCount = 0;
  let originMismatches = 0;
  let parseNulls = 0;
  let validationErrors = 0;
  let tokensAccepted = 0;
  let firstTokenTs = null;

  const iframe = document.createElement('iframe');
  iframe.id = 'tokenFrame';
  iframe.name = 'tokenFrame';
  iframe.className = 'iframe';
  iframe.setAttribute('frameborder', '0');
  iframe.setAttribute('scrolling', 'no');
  iframe.src = buildIframeSrc(env);
  containerEl.appendChild(iframe);

  const allowedOrigin =
    env === 'uat'
      ? 'https://boltgw-uat.cardconnect.com'
      : 'https://boltgw.cardconnect.com';

  function previewData(data) {
    try {
      const s = typeof data === 'string' ? data : JSON.stringify(data);
      // redact long digit runs and obvious tokens
      const redacted = s
        .replace(/[0-9]{6,}/g, '[[redacted-digits]]')
        .replace(/"token"\s*:\s*"[^"]+"/gi, '"token":"[[redacted-token]]"')
        .slice(0, 128);
      return redacted;
    } catch (_e) {
      return '[unserializable]';
    }
  }

  console.log('[CC-HIFT] attach', {
    sessionId: sessionId,
    env: env,
    allowedOrigin: allowedOrigin,
    iframeSrc: iframe.src,
    ts: new Date(attachTs).toISOString(),
  });

  let isActive = true;

  iframe.addEventListener('load', function () {
    console.log('[CC-HIFT] iframe-load', { sessionId: sessionId });
  });
  iframe.addEventListener('error', function () {
    console.log('[CC-HIFT] iframe-error', { sessionId: sessionId });
  });

  function handleMessage(event) {
    console.log('handleMessage', {
      event,
      isActive,
      allowedOrigin,
    });
    if (!isActive) return;
    msgCount += 1;
    const originOk = !!event && event.origin === allowedOrigin;

    console.log('[CC-HIFT] message', {
      sessionId: sessionId,
      n: msgCount,
      originOk: originOk,
      origin: event && event.origin,
      sameSource: !!(
        iframe.contentWindow &&
        event &&
        event.source === iframe.contentWindow
      ),
      preview: previewData(event && event.data),
    });

    if (!originOk) {
      originMismatches += 1;
      return;
    }

    const parsed = parseMessageData(event.data);
    if (!parsed) {
      parseNulls += 1;
      if (msgCount < 10 || msgCount % 10 === 0) {
        console.log('[CC-HIFT] parse-null', { sessionId: sessionId, n: msgCount });
      }
      return;
    }
    if (parsed.error) {
      validationErrors += 1;
      console.log('[CC-HIFT] validation/error', { sessionId: sessionId, n: msgCount });
      try {
        if (onError) onError(true);
      } catch (_e) {}
      return;
    }

    tokensAccepted += 1;
    if (!firstTokenTs) firstTokenTs = Date.now();
    console.log('[CC-HIFT] token-accepted', {
      sessionId: sessionId,
      ageMs: firstTokenTs - attachTs,
      expiryLen: parsed.expiry ? String(parsed.expiry).length : 0,
    });
    try {
      if (onToken) onToken(parsed);
    } catch (_e) {}
  }

  window.addEventListener('message', handleMessage, false);

  function detach() {
    console.log('detach');
    isActive = false;
    window.removeEventListener('message', handleMessage, false);
    try {
      if (iframe && iframe.parentNode === containerEl) {
        containerEl.removeChild(iframe);
      }
    } catch (_e) {}
    console.log('[CC-HIFT] detach', {
      sessionId: sessionId,
      lifetimeMs: Date.now() - attachTs,
      msgCount: msgCount,
      originMismatches: originMismatches,
      parseNulls: parseNulls,
      validationErrors: validationErrors,
      tokensAccepted: tokensAccepted,
      gotFirstTokenMs: firstTokenTs ? firstTokenTs - attachTs : null,
    });
  }

  return detach;
}

export default { attach };
